import L from "leaflet";
import "leaflet.markercluster";
import { makeClusterDivIcon } from "./markerHandler";
import { POPUP_REOPEN_DELAY, DEFAULT_MARKER_COLOR } from "../constants";

/**
 * Creates a configured MarkerClusterGroup for a set of points.
 * Supports multi-color clusters and custom cluster icon rendering.
 */
export function createClusterGroup(
  colors: Set<string>,
  count: number,
  size: number,
  maxClusterRadius = 80
): L.MarkerClusterGroup {
  const colorArr = Array.from(colors);
  // Use all colors if >1, otherwise single color or fallback
  const clusterColors: string | string[] =
    colorArr.length > 1 ? colorArr : colorArr[0] || DEFAULT_MARKER_COLOR;
  // Calculate spiderfy spread multiplier based on cluster size
  const spreadMult = Math.min(4, 1.2 + (count + 500) * 0.06);

  return L.markerClusterGroup({
    chunkedLoading: true,
    maxClusterRadius,
    showCoverageOnHover: false,
    zoomToBoundsOnClick: false,
    spiderfyOnMaxZoom: true,
    spiderfyDistanceMultiplier: spreadMult,
    spiderLegPolylineOptions: {
      weight: 1,
      color: colorArr[0] || DEFAULT_MARKER_COLOR,
      opacity: 0.6,
    },
    iconCreateFunction: (cluster) =>
      makeClusterDivIcon(cluster.getChildCount(), clusterColors, size),
  });
}

/**
 * Tracks cluster state for popup restoration and spiderfy logic.
 */
type Track = {
  group: L.MarkerClusterGroup;
  sample: L.Marker;
  wasOpen: boolean;
};

/**
 * Handles the 'spiderfied' event for a cluster group.
 * Marks the cluster as open and restores popups for child markers.
 */
export function handleSpiderfied(
  track: Track,
  clusterGroup: L.MarkerClusterGroup,
  openIds: Set<string>,
  reopenSavedPopupsFor: (parent: any, openIds: Set<string>, delay?: number) => void
) {
  return (e: any) => {
    track.wasOpen = true;
    const parent = e?.cluster || (clusterGroup as any).getVisibleParent(track.sample);
    setTimeout(() => reopenSavedPopupsFor(parent, openIds, POPUP_REOPEN_DELAY), POPUP_REOPEN_DELAY);
  };
}

/**
 * Handles the 'unspiderfied' event for a cluster group.
 * Marks the cluster as closed.
 */
export function handleUnspiderfied(track: Track) {
  return () => {
    track.wasOpen = false;
  };
}

/**
 * Handles the 'clusterclick' event for a cluster group.
 * Toggles spiderfy state and restores or closes popups as needed.
 */
export function handleClusterclick(
  tracked: Track[],
  clusterGroup: L.MarkerClusterGroup,
  openIds: Set<string>,
  reopenSavedPopupsFor: (parent: any, openIds: Set<string>, delay?: number) => void
) {
  return (e: any) => {
    if (e?.originalEvent) {
      L.DomEvent.preventDefault(e.originalEvent);
      L.DomEvent.stopPropagation(e.originalEvent);
    }
    const t = tracked.find((x) => x.group === clusterGroup);
    if (!t) return;

    t.wasOpen = !t.wasOpen;
    const parent: any = (clusterGroup as any).getVisibleParent(t.sample) || e.layer;
    if (t.wasOpen) {
      parent?.spiderfy?.();
      setTimeout(() => reopenSavedPopupsFor(parent, openIds, POPUP_REOPEN_DELAY), POPUP_REOPEN_DELAY);
    } else {
      // Temporarily suppress popup cleanup during unspiderfy
      let suppressCloseCleanup = true;
      (clusterGroup as any).unspiderfy();
      setTimeout(() => (suppressCloseCleanup = false), 0);
    }
    (clusterGroup as any).refreshClusters?.(parent);
  };
}

/**
 * Handles the 'animationend' event for a cluster group.
 * Re-spiderfies the cluster and restores popups if it was previously open.
 */
export function handleAnimationend(
  track: Track,
  clusterGroup: L.MarkerClusterGroup,
  openIds: Set<string>,
  reopenSavedPopupsFor: (parent: any, openIds: Set<string>, delay?: number) => void
) {
  return () => {
    if (!track.wasOpen) return;
    setTimeout(() => {
      const parent: any = (clusterGroup as any).getVisibleParent(track.sample);
      parent?.spiderfy?.();
      setTimeout(() => reopenSavedPopupsFor(parent, openIds, POPUP_REOPEN_DELAY), POPUP_REOPEN_DELAY);
    }, 0);
  };
}